import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

import Car from "../images/Carblue.png";
import Motorcycle from "../images/Motorcycle.png";
import Nightcars from "../images/Nightcars.png";
import Engine from "../images/Engine.png";
import RealTime from "../images/RealTime.jpg";
import Installation from "../images/Installation.jpg";

export default function Services() {

  // 🔥 BREADCRUMB SCHEMA ADDED
  // This injects the schema directly into the document head safely
  React.useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://www.ledotprime.com"
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Services",
          item: "https://www.ledotprime.com/services"
        }
      ]
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);
  // 🔥 END BREADCRUMB SCHEMA


  const services = [
    {
      title: "GPS Car Tracking",
      description:
        "Track your vehicle anytime, anywhere with real-time location updates and instant alerts.",
      image: Car,
    },
    {
      title: "Motorbike Tracking",
      description:
        "Protect your bike with GPS monitoring, anti-theft alerts, and activity reports.",
      image: Motorcycle,
    },
    {
      title: "Fleet Monitoring",
      description:
        "Monitor all your vehicles, optimize routes, reduce fuel loss, and improve efficiency.",
      image: Nightcars,
    },
    {
      title: "Engine Lock/Unlock",
      description:
        "Remotely control your vehicle engine for security and theft prevention.",
      image: Engine,
    },
    {
      title: "Real-Time Location",
      description:
        "Get live location updates for your vehicles or fleet, anytime, anywhere.",
      image: RealTime,
    },
    {
      title: "Professional Installation",
      description:
        "Our experts ensure your GPS system is installed correctly, quickly, and reliably.",
      image: Installation,
    },
  ];

  const containerVariants = {
    hidden: {},
    visible: {
      transition: { staggerChildren: 0.15 },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: "spring", stiffness: 110, damping: 16 },
    },
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { type: "spring", stiffness: 110, damping: 18 },
    },
  };

  const textVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: "spring", stiffness: 120, damping: 18 },
    },
  };

  return (
    <div className="w-full flex flex-col items-center bg-white text-black pt-20">

      {/* HERO SECTION */}
      <section className="w-full max-w-6xl px-4 md:px-0 text-center m-10">
        <motion.h1
          className="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-tight mb-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          Our Services
        </motion.h1>

        <motion.p
          className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-2"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          Ledot Prime provides next-level vehicle security solutions designed to give you
          control, insights, and total peace of mind.
        </motion.p>
      </section>

      {/* SERVICES GRID */}
      <motion.section
        className="
          w-full max-w-6xl 
          px-4 md:px-0 
          grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 
          gap-6 sm:gap-8 pb-16
        "
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {[...services.slice(3), ...services.slice(0, 3)].map((service, index) => (
          <motion.div
            key={index}
            className="
              bg-white rounded-3xl shadow-xl overflow-hidden 
              hover:shadow-2xl 
              transition-all duration-300
            "
            variants={cardVariants}
            whileHover={{ scale: 1.04 }}
          >
            <motion.img
              src={service.image}
              alt={service.title}
              className="w-full h-52 sm:h-56 object-cover"
              variants={imageVariants}
              whileHover={{ scale: 1.1 }}
            />

            <div className="p-5 sm:p-6">
              <motion.h3
                className="text-xl sm:text-2xl font-bold mb-2 tracking-tight"
                variants={textVariants}
              >
                {service.title}
              </motion.h3>

              <motion.p
                className="text-gray-600 text-sm sm:text-base"
                variants={textVariants}
              >
                {service.description}
              </motion.p>
            </div>
          </motion.div>
        ))}
      </motion.section>

      {/* CTA SECTION */}
      <section className="w-full bg-black text-white py-16 sm:py-20 px-6 text-center mt-4">
        <motion.h2
          className="text-3xl sm:text-4xl font-extrabold mb-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          Ready to Protect Your Vehicle?
        </motion.h2>

        <motion.p
          className="text-gray-300 text-base sm:text-lg max-w-xl mx-auto mb-8"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          Get started with Ledot Prime today and enjoy premium vehicle security and tracking.
        </motion.p>

        <Link to="/contact">
          <motion.button
            className="
              bg-white text-black font-semibold 
              px-10 py-3 
              rounded-xl text-sm sm:text-base
              hover:bg-gray-200 
              transition-all
            "
            whileHover={{ scale: 1.05 }}
          >
            Contact Us
          </motion.button>
        </Link>
      </section>

    </div>
  );
}
